import '../broiler/poultryDesign.css';
import Button from '../shared/Button.js';
import React from 'react';
import {useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';

import RecordCollaborationSection from '../shared/RecordCollaborationSection.tsx';

import RecordSequenceNav from '../shared/RecordSequenceNav.tsx';
import {recordSeqNavOptions} from '../lib/recordSequence.js';

import InlineNotice from '../shared/InlineNotice.jsx';
import {useConfirmedDraftAutosave} from '../shared/useConfirmedDraftAutosave.js';
import RecordPageLoadError from '../shared/RecordPageLoadError.jsx';

import {
  RecordPageFrame,
  RecordPageLoading,
  RecordPageNotFound,
  RecordPageBody,
  RecordBackLink,
  RecordTitle,
} from '../shared/RecordPageShell.jsx';
import {recordFieldRowClass, recordFieldLabel, recordControl, recordTextarea} from '../shared/recordPageControls.jsx';

import Badge from '../shared/Badge.jsx';
import {GroupDot} from '../shared/GroupPillPicker.js';
import {buildLayerGroupPalette, layerGroupColor, layerGroupTint} from './layerGroupPalette.js';

import StatusText from '../shared/StatusText.jsx';
import {computeProjectedCount, computeHousingDisplayCount} from '../lib/layerHousing.js';
import {getHousingCap, computeHousingStats} from './layerBatchStats.js';
import {useQueryClient} from '@tanstack/react-query';
import {useAuth} from '../contexts/AuthContext.js';
import {layerPatch, layerRejectionMessage, type LayerOpOutcome} from '../lib/layerOperationsApi.js';
import {LAYER_FIELD_LABELS, LAYER_HOUSING_FIELDS, createLayerOpRunner} from './layerRecordOps.js';
import PigConflictNotice, {type PigConflictState} from '../pig/PigConflictNotice.js';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {LayerRecordAppStoreData, LayerRecordCoreData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import Header from '../shared/Header.tsx';
import type {AuthState} from '../contexts/AuthContext.js';
import type {Notice} from '../shared/InlineNotice.js';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import type {ISODateInput} from '../lib/dateUtils.js';
import type {RecordSeqEntry, RecordSeqId} from '../lib/recordSequence.js';
import type {LayerDailyRow, EggDailyRow, LayerStatTotals} from './layerBatchStats.js';

// The layer_housings row as THIS page reads and rewrites it. Deliberately not
// layerBatchStats' LayerHousingRow: that type describes the five columns the
// STATS module needs, and this page also reads/writes status, current_count,
// current_count_date and notes. Both are partial views of the same table.
type HousingRow = {
  id: string;
  batch_id?: string | null;
  housing_name?: string | null;
  status?: string | null;
  current_count?: number | null;
  current_count_date?: string | null;
  start_date?: string | null;
  retired_date?: string | null;
  notes?: string | null;
};

// A layer_batches row, reduced to the two columns rendered here.
type BatchRow = {id: string; name?: string | null};

// A legacy layer group, reduced to the two fields the housing-name picker
// reads. `name` is not `| null`: LayersView, the only writer of these rows,
// builds every one of them from a form whose name field is a string.
type GroupRow = {id?: string | number; name?: string};
// The housing edit draft. Every field is a string because it is bound straight
// to form controls; current_count is parsed back to a number on save.
type HousingDraft = {
  housing_name: string;
  status: string;
  current_count: string;
  start_date: string;
  retired_date: string;
  notes: string;
};

const EMPTY_HOUSING_DRAFT: HousingDraft = {
  housing_name: '',
  status: 'active',
  current_count: '',
  start_date: '',
  retired_date: '',
  notes: '',
};

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// LayerBatchesView builds this object literal explicitly at BOTH of this page's
// mount sites — LayerBatchesRouter's `/layer/housings/` branch for the shapes
// the boot release did not audit, and LayerHousingRecordMount for the released
// record route — and they are asserted character-identical in
// tests/static/layer_housings_detail_boot_release_static.test.js. Every prop
// below is one they really pass, and after the boot release there are three.
//
// FOUR PROPS LEFT AND DID NOT COME BACK. layerGroups, layerBatches and
// layerHousings were App's former-eager boot results, threaded here by
// LayerEntry through the router; this page reads all three for itself now,
// through layer.record.core and layer.record.appStore, together with the two
// metric feeds it used to paginate through a dynamic-table helper — the table
// arrived as a PARAMETER, so both of those reads inventoried as `from:~table`
// and could prove ownership of neither literal table.
//
// setLayerHousings LEFT WITH THE BOOT RELEASE, and it was never the database
// write. Since migration 242 (R4) `persistHousing` and `retireNow` are
// housing.update calls to layer_apply_operation through
// src/layer/layerRecordOps.ts: one audited operation each, checked against the
// persisted row, with the hen-count anchor rule (the count date is the server's
// farm date) and the Activity written by the server in the same transaction.
// This page keeps the decision to run them, the error branches and the overlay
// repaints. What left is
// App's former POST-write fan-out — `persistLayerHousings`, which was only
// `setLayerHousings(nb)` plus maintenance notification and performed no write of
// its own. Both halves are redundant on a released surface: this page owns and
// revalidates its record resources, and no route transition repopulates a global
// LayerContext directory. The batch sibling retains its local update helper
// because it can create or delete housings from that page.
type LayerHousingPageProps = {
  sb: SupabaseClient;
  fmt: (iso: ISODateInput) => string;
  authState: AuthState;
};

const LAYER_RECORD_PARAMS = Object.freeze({});
// The record's editable cards (R25).
type HousingEditorCard = 'details' | 'notes';

// R08: a record card is radius 18 on --shadow-card; card titles are 16/800.
const recordCardS: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-card)',
  boxShadow: 'var(--shadow-card)',
};
const cardTitleS: React.CSSProperties = {fontSize: 16, fontWeight: 800, color: 'var(--ink)'};
// R25: a card's one text action (Edit), brand ink, no button chrome.
const cardEditActionS: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '6px 2px',
  minHeight: 32,
  color: 'var(--brand)',
  fontFamily: 'inherit',
  fontSize: 13.5,
  fontWeight: 700,
  cursor: 'pointer',
};

const EMPTY_LAYER_RECORD_ROWS: unknown[] = [];

export default function LayerHousingPage({sb, fmt, authState}: LayerHousingPageProps) {
  // 242: every write on this page is ONE layer_apply_operation; the server
  // writes its Activity in the same transaction.
  const queryClient = useQueryClient();
  const {setSaveStatus} = useAuth();
  const [applyLayerOp] = React.useState(() => createLayerOpRunner({sb, onSaveStatus: setSaveStatus, queryClient}));
  const [housingConflict, setHousingConflict] = React.useState<PigConflictState | null>(null);
  // ── The reads this page owns ─────────────────────────────────────────────
  // The same two resources its batch sibling uses, because the router's branch
  // is what makes them two surfaces, not the data.
  const core = useRouteResource(RESOURCE.LAYER_RECORD_CORE, LAYER_RECORD_PARAMS);
  const appStore = useRouteResource(RESOURCE.LAYER_RECORD_APP_STORE, LAYER_RECORD_PARAMS);
  const coreData = core.data as LayerRecordCoreData | null;
  const appStoreData = appStore.data as LayerRecordAppStoreData | null;

  // This page WRITES housings and never batches, so it carries one overlay. It
  // exists because the page used to re-render from the App context array its
  // setter had just updated, and a query result cannot do that synchronously.
  // Since the boot release the overlay is the WHOLE of the local publish: the
  // App fan-out it used to call afterwards is gone, so this is a plain state
  // setter with a name that still says what it means at the two call sites.
  const [housingOverlay, setHousingOverlay] = React.useState<unknown[] | null>(null);
  const serverHousings = coreData?.layerHousings || EMPTY_LAYER_RECORD_ROWS;
  const setLayerHousings = React.useCallback((next: HousingRow[]) => {
    setHousingOverlay(next);
  }, []);

  // The one place this page states what it believes the three opaque arrays
  // hold; every field read below is then checked against these types.
  const layerGroups = (appStoreData?.layerGroups || EMPTY_LAYER_RECORD_ROWS) as GroupRow[];
  const layerBatches = (coreData?.layerBatches || EMPTY_LAYER_RECORD_ROWS) as BatchRow[];
  const layerHousings = (housingOverlay ?? serverHousings) as HousingRow[];
  const navigate = useNavigate();
  // `location.state` is `unknown` by design (src/app/navigation/appLocation.ts): the
  // write side normalized it and no consumer may read it without proving its
  // shape. The route state this page reads is exactly the one
  // recordSeqNavOptions writes, so that proof is applied ONCE, here at the hook
  // call, instead of at the read — which leaves the read the same expression it
  // has always been. The location shape is taken from the facade hook itself, so
  // no facade internal is imported.
  const location = useLocation() as Omit<ReturnType<typeof useLocation>, 'state'> & {
    state?: {recordSeq?: RecordSeqEntry[]} | null;
  };
  const housingId = location.pathname.slice('/layer/housings/'.length);
  // Originating list order handed through route state; absent on direct links
  // and notification/deep-link opens, so the controls hide.
  const recordSeq = location.state?.recordSeq || null;
  function navigateSeq(id: RecordSeqId) {
    navigate('/layer/housings/' + id, recordSeqNavOptions(recordSeq));
  }

  const role = authState && authState.role;
  const canEdit = role === 'admin' || role === 'management';

  // Resolve housing + parent batch from the directories this page loaded. They
  // used to come from App's boot arrays, which main.jsx guaranteed were
  // populated before any layer route rendered; the guarantee is now this page's
  // own `loading` gate, checked below before either value is read, so a cold
  // direct load to /layer/housings/<id> still cannot race an empty by-id read.
  const housing = React.useMemo(
    () => (layerHousings || []).find((h) => h.id === housingId) || null,
    [layerHousings, housingId],
  );
  const savedHousing = React.useRef(housing);
  savedHousing.current = housing;
  const parentBatch = React.useMemo(() => {
    const bid = housing && housing.batch_id;
    return bid ? (layerBatches || []).find((b) => b.id === bid) || null : null;
  }, [layerBatches, housing]);
  const rawLayerDailys = (coreData?.layerDailys || EMPTY_LAYER_RECORD_ROWS) as LayerDailyRow[];
  const rawEggDailys = (coreData?.eggDailys || EMPTY_LAYER_RECORD_ROWS) as EggDailyRow[];
  const loading = core.loading || appStore.loading;
  const resourceError = core.error || appStore.error;
  const loadError: Notice | null = resourceError
    ? {
        kind: 'error',
        message:
          'Could not load layer housing metrics: ' +
          ((resourceError as {message?: string}).message || String(resourceError)),
      }
    : null;
  const retirementHomeTarget =
    parentBatch?.name === 'Retirement Home' ? '/layer/batches/' + parentBatch.id + location.hash : null;
  React.useEffect(() => {
    if (!loading && !resourceError && retirementHomeTarget) navigate(retirementHomeTarget, {replace: true});
  }, [loading, resourceError, retirementHomeTarget, navigate]);
  const [notice, setNotice] = React.useState<Notice | null>(null);
  // R25 (owner amendment 2026-10-24: autosave with Done): the record reads
  // first and one card at a time flips to inputs in place. Both cards edit the
  // SAME draft (hForm) through the same confirmed autosave.
  const [editingCard, setEditingCard] = React.useState<HousingEditorCard | null>(null);
  const [hForm, setHForm] = React.useState<HousingDraft>(EMPTY_HOUSING_DRAFT);
  const [err, setErr] = React.useState('');
  const hFormRef = React.useRef(hForm);
  const retiringHousing = React.useRef(false);
  // R25: Done and a card switch both await the same flush. This one hold covers
  // both (and blocks Retire, edits and navigation while it is held), the way
  // the batch page's closingBatch does, so a Done that clears the draft can
  // never race a switch that opens a card over it.
  const housingTransition = React.useRef(false);
  const [housingActionBusy, setHousingActionBusy] = React.useState(false);
  const {controller: housingDraft, state: housingSaveState} = useConfirmedDraftAutosave<HousingDraft>({
    ownerKey: 'layer.housing.editor',
    debounceMs: 1500,
    isBusy: () => retiringHousing.current || housingTransition.current,
    validate: (draft) => {
      if (draft.housing_name.trim()) return true;
      setErr('Housing name is required.');
      return false;
    },
    persist: (draft) => persistHousing(buildHousingRec(draft)),
  });
  const housingSaving = housingSaveState === 'saving';
  const housingPending = housingSaveState !== 'idle';
  React.useEffect(() => {
    if (!housingDraft.isDirty()) setEditingCard(null);
  }, [housingId, housingDraft]);

  // The paginated metric loader that used to live here belongs to
  // layer.record.core now, read above. Its two dynamic-table calls are retired
  // in tests/fixtures/render-read-sites.json: with the table as a parameter
  // they inventoried as `from:~table` and could prove ownership of neither
  // literal table.
  // A Retry is a fresh server answer, so the local overlay goes with it.
  const loadAll = React.useCallback(async () => {
    setHousingOverlay(null);
    await Promise.all([core.refetch(), appStore.refetch()]);
  }, [appStore, core]);

  React.useEffect(() => {
    setNotice(null);
  }, [housingId, authState]);

  React.useEffect(() => {
    if (!loading && location.hash) {
      const anchor = location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(anchor);
        if (el) el.scrollIntoView({behavior: 'smooth', block: 'center'});
      }, 200);
    }
  }, [loading, location.hash]);
  function openEdit(card: HousingEditorCard) {
    if (!canEdit || !housing || retiringHousing.current || housingTransition.current) return;
    if (housingDraft.isDirty()) {
      setEditingCard(card);
      return;
    }
    setErr('');
    setNotice(null);
    const draft = {
      housing_name: housing.housing_name || '',
      status: housing.status || 'active',
      current_count: housing.current_count != null ? String(housing.current_count) : '',
      start_date: housing.start_date || '',
      retired_date: housing.retired_date || '',
      notes: housing.notes || '',
    };
    hFormRef.current = draft;
    setHForm(draft);
    setEditingCard(card);
  }

  // A card's Edit. With another card open, that card settles FIRST through the
  // same flush Done uses; on success the shared draft carries over, on a failed
  // save the open editor stays on screen with its draft, so nothing is lost.
  async function editCard(card: HousingEditorCard) {
    if (!canEdit || !housing || retiringHousing.current || housingTransition.current || editingCard === card) return;
    if (editingCard === null) {
      openEdit(card);
      return;
    }
    housingTransition.current = true;
    setHousingActionBusy(true);
    try {
      if (!(await flushHousingAutosave())) return;
      setErr('');
      setEditingCard(card);
    } finally {
      housingTransition.current = false;
      setHousingActionBusy(false);
    }
  }

  function buildHousingRec(formSnapshot: HousingDraft): HousingRow {
    const newVal = formSnapshot.current_count !== '' ? parseInt(formSnapshot.current_count) : null;
    // String() around a column that is already numeric in SQL: parseInt is
    // DECLARED to take a string and did the same ToString conversion itself.
    const oldVal = housing && housing.current_count != null ? parseInt(String(housing.current_count)) : null;
    const stamp =
      newVal !== oldVal ? todayStr() : housing && housing.current_count_date ? housing.current_count_date : null;
    return {
      // `housing` is non-null at every call site (both are behind a housing
      // guard), but the spread is written for the declared type rather than the
      // reachable one — `{...null}` is `{}`, exactly as it was in JavaScript.
      ...savedHousing.current,
      id: housing ? housing.id : '',
      housing_name: formSnapshot.housing_name,
      status: formSnapshot.status || 'active',
      current_count: newVal,
      current_count_date: stamp,
      start_date: formSnapshot.start_date || null,
      retired_date: formSnapshot.retired_date || null,
      notes: formSnapshot.notes || null,
    };
  }

  async function persistHousing(rec: HousingRow) {
    if (!rec.housing_name) return false;
    const cap = getHousingCap(rec.housing_name);
    if ((rec.current_count || 0) > cap) {
      setErr('⚠ ' + rec.housing_name + ' capacity is ' + cap + ' birds. You have ' + rec.current_count + '.');
    } else {
      setErr('');
    }
    // 242: ONE housing.update of only the fields that changed, checked against
    // the persisted row. A count change also expects the stored count date, so
    // a report that re-anchored the housing meanwhile is a conflict, not an
    // overwrite; the server stamps the date and logs status/field changes.
    const stored = savedHousing.current;
    if (!stored) return false;
    const {fields, expected, change} = layerPatch(stored, rec, LAYER_HOUSING_FIELDS);
    if (!fields.length) return true;
    const outcome = await applyLayerOp({
      operation: 'housing.update',
      target: {housing_id: stored.id},
      expected,
      change,
    });
    return settleHousingOutcome(outcome);
  }

  // One reading of every outcome for this editor (and the Retire action).
  function settleHousingOutcome(outcome: LayerOpOutcome): boolean {
    if (outcome.status === 'saved' || outcome.status === 'no_change') {
      const saved = outcome.rows?.housing as HousingRow | undefined;
      if (saved) {
        savedHousing.current = saved;
        if (typeof setLayerHousings === 'function') {
          const nextHousings = (layerHousings || []).map((x) => (x.id === saved.id ? saved : x));
          setLayerHousings(nextHousings);
        }
      }
      if (outcome.status === 'saved' && outcome.mustReread) void loadAll();
      setHousingConflict(null);
      return true;
    }
    if (outcome.status === 'conflict') {
      const conflicts = outcome.conflicts;
      void loadAll();
      setHousingConflict({
        conflicts,
        labels: LAYER_FIELD_LABELS,
        useLatest: async () => {
          await loadAll();
          setHousingConflict(null);
          const merged = {...hFormRef.current} as Record<string, string>;
          for (const c of conflicts)
            if (c.field in merged) merged[c.field] = c.current == null ? '' : String(c.current);
          const next = merged as HousingDraft;
          hFormRef.current = next;
          setHForm(next);
          housingDraft.update(next);
        },
      });
      return false;
    }
    if (outcome.status === 'unknown') void loadAll();
    setErr(
      outcome.status === 'rejected'
        ? 'Not saved: ' + layerRejectionMessage(outcome)
        : outcome.status === 'refused'
          ? 'Not saved: ' + outcome.reason
          : outcome.message,
    );
    return false;
  }

  function scheduleHousingAutosave(formSnapshot: HousingDraft) {
    housingDraft.update(formSnapshot);
  }

  async function flushHousingAutosave() {
    return housingDraft.flush();
  }

  async function closeForm() {
    if (retiringHousing.current || housingTransition.current) return;
    housingTransition.current = true;
    setHousingActionBusy(true);
    try {
      if (!(await flushHousingAutosave())) return;
      setEditingCard(null);
      hFormRef.current = EMPTY_HOUSING_DRAFT;
      setHForm(EMPTY_HOUSING_DRAFT);
      setErr('');
    } finally {
      housingTransition.current = false;
      setHousingActionBusy(false);
    }
  }

  function updHousing(updater: HousingDraft | ((prev: HousingDraft) => HousingDraft)) {
    if (retiringHousing.current || housingTransition.current) return;
    const next = typeof updater === 'function' ? updater(hFormRef.current) : updater;
    hFormRef.current = next;
    setHForm(next);
    scheduleHousingAutosave(next);
  }

  async function retireNow() {
    if (!canEdit || !housing || retiringHousing.current || housingTransition.current) return;
    retiringHousing.current = true;
    setHousingActionBusy(true);
    try {
      if (!(await flushHousingAutosave())) return;
      setNotice(null);
      // 242: housing.update {status: 'retired'}; the server keeps an existing
      // retired_date or stamps the farm date, and logs the change itself.
      const current = savedHousing.current || housing;
      const outcome = await applyLayerOp({
        operation: 'housing.update',
        target: {housing_id: current.id},
        expected: {status: current.status ?? null},
        change: {status: 'retired'},
      });
      if (outcome.status !== 'saved' && outcome.status !== 'no_change') {
        setNotice({
          kind: 'error',
          message:
            'Could not retire: ' +
            (outcome.status === 'rejected'
              ? layerRejectionMessage(outcome)
              : outcome.status === 'conflict'
                ? 'this housing changed meanwhile. Reload and try again.'
                : outcome.status === 'refused'
                  ? outcome.reason
                  : outcome.message),
        });
        if (outcome.status === 'conflict' || outcome.status === 'unknown') void loadAll();
        return;
      }
      settleHousingOutcome(outcome);
      setEditingCard(null);
    } catch (error) {
      setNotice({
        kind: 'error',
        message: 'Could not retire: ' + (error instanceof Error ? error.message : String(error)),
      });
    } finally {
      retiringHousing.current = false;
      setHousingActionBusy(false);
    }
  }

  function confirmRetire() {
    if (!canEdit || !housing) return;
    if (typeof window === 'undefined' || typeof window._wcfConfirm !== 'function') return;
    window._wcfConfirm('Retire ' + housing.housing_name + '?', () => retireNow(), 'Retire');
  }

  if (loading) {
    return <RecordPageLoading Header={Header} />;
  }

  if (loadError) {
    return (
      <RecordPageLoadError
        backLabel="Back to Layer Batches"
        onBack={() => navigate('/layer/batches')}
        notice={loadError}
        onRetry={loadAll}
        maxWidth={960}
        data-layer-housing-load-error="true"
      />
    );
  }

  if (!housing) {
    return (
      <RecordPageNotFound
        Header={Header}
        backLabel="Back to Layer Batches"
        onBack={() => navigate('/layer/batches')}
        message="Housing not found."
      />
    );
  }

  if (retirementHomeTarget) return <RecordPageLoading Header={Header} />;

  const hStatsMap = computeHousingStats(parentBatch ? [parentBatch] : [], [housing], rawLayerDailys, rawEggDailys);
  // `Partial<LayerStatTotals>` because the `|| {}` fallback is real: a housing
  // with no matching dailys is absent from the map, and every read below is
  // already written for a missing value.
  const hs: Partial<LayerStatTotals> = hStatsMap[housing.id] || {};
  const displayCount = computeHousingDisplayCount(housing, rawLayerDailys, layerHousings);
  const proj = computeProjectedCount(housing, rawLayerDailys, layerHousings);
  // Headline farm count = mortality-adjusted projected hens. computeProjectedCount
  // already falls back to the latest positive daily count when current_count is a
  // stale/empty 0, so an active housing (e.g. Eggmobile 3) never surfaces a bare
  // "Physical: 0" as the meaningful live count. A retired housing with an
  // intentional newer zero keeps anchor/projected 0 (handled in the helper).
  const liveHens = proj ? proj.projected : null;
  // Number() around anchor/projected/current_count: computeProjectedCount can
  // return null for either, and `null > 0` / `null < x` are relational
  // comparisons that already coerced through ToNumber. Number(null) is 0 and
  // Number(undefined) is NaN, so every one of these tests keeps its old result.
  const liveHensLow = !!(proj && Number(proj.anchor) > 0 && Number(proj.projected) < Number(proj.anchor) * 0.9);
  const anchorFromFallback = !!(proj && Number(proj.anchor) > 0 && !(Number(housing.current_count) > 0));
  const showAnchorCtx = !!(proj && proj.anchorDate && (Number(proj.mortSince) > 0 || anchorFromFallback));
  const cap = getHousingCap(housing.housing_name);
  const util = displayCount && cap ? Math.round((displayCount / cap) * 100) : null;
  const isActive = housing.status === 'active';
  // R06: a housing is a group occurrence, colored by canonical group order.
  const housingDot = layerGroupColor(buildLayerGroupPalette(layerGroups), housing.housing_name);
  const backTarget = parentBatch ? '/layer/batches/' + parentBatch.id : '/layer/batches';
  // Label without the arrow — RecordBackLink renders the leading "← ".
  const backLabel = parentBatch ? 'Back to ' + parentBatch.name : 'Back to Layer Batches';

  // ---- R25 per-card editors ----------------------------------------------
  // The former Edit Housing modal's fields, unchanged and in order, split by
  // card. Both edit the one shared draft through the same confirmed autosave.
  const editorFieldsetS: React.CSSProperties = {border: 0, margin: 0, padding: 0, minWidth: 0};
  // data-layer-housing-form-modal is the editor's existing hook (R34 keeps
  // data-* hooks); data-layer-housing-editor names which card is open.
  const editorAttrs = (card: HousingEditorCard) => ({
    'data-layer-housing-form-modal': '1',
    'data-layer-housing-editor': card,
  });
  const cardHead = (card: HousingEditorCard, title: string) => (
    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12}}>
      <div style={cardTitleS}>{title}</div>
      {canEdit && editingCard !== card && (
        <button
          type="button"
          className="wcf-clickable"
          style={cardEditActionS}
          disabled={housingActionBusy}
          data-layer-housing-edit={housing.id}
          data-layer-housing-edit-card={card}
          onClick={() => void editCard(card)}
        >
          Edit
        </button>
      )}
    </div>
  );
  const detailsEditor = (
    <>
      <div className={recordFieldRowClass}>
        <span style={recordFieldLabel}>Housing (Layer Group) *</span>
        <select
          value={hForm.housing_name}
          onChange={(e) => updHousing((f) => ({...f, housing_name: e.target.value}))}
          style={recordControl}
        >
          <option value="">{'Select housing…'}</option>
          {(layerGroups || []).map((g) => {
            const owningHousing = (layerHousings || []).find(
              (x) => x.housing_name === g.name && x.status === 'active' && x.id !== housing.id,
            );
            const locked = !!owningHousing && housing.housing_name !== g.name;
            const owningBatch = owningHousing
              ? (layerBatches || []).find((b) => b.id === owningHousing.batch_id)
              : null;
            const label = g.name + (locked ? ' ⚠ In use' + (owningBatch ? ' by ' + owningBatch.name : '') : '');
            return (
              <option key={g.id} value={g.name} disabled={locked}>
                {label}
              </option>
            );
          })}
        </select>
      </div>

      {hForm.housing_name &&
        (() => {
          const c = getHousingCap(hForm.housing_name);
          return (
            c < 9999 && (
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--warn-ink)',
                  background: 'var(--warn-soft)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  padding: '6px 10px',
                  marginBottom: 4,
                }}
              >
                {'⚠ Capacity: ' + c + ' birds max'}
              </div>
            )
          );
        })()}

      <div className={recordFieldRowClass}>
        <span style={recordFieldLabel}>Current Count</span>
        <div>
          <input
            type="number"
            min="0"
            value={hForm.current_count || ''}
            onChange={(e) => updHousing((f) => ({...f, current_count: e.target.value}))}
            style={recordControl}
          />
          {(() => {
            const oldVal = housing.current_count != null ? parseInt(String(housing.current_count)) : null;
            const newVal = hForm.current_count !== '' ? parseInt(hForm.current_count) : null;
            if (newVal !== oldVal) {
              return (
                <div style={{fontSize: 10, color: 'var(--ok-ink)', marginTop: 4, fontWeight: 600}}>
                  {'Will be stamped: ' + fmt(todayStr())}
                </div>
              );
            }
            if (housing.current_count_date) {
              return (
                <div style={{fontSize: 10, color: 'var(--ink-faint)', marginTop: 4}}>
                  {'Last set: ' + fmt(housing.current_count_date)}
                </div>
              );
            }
            return null;
          })()}
        </div>
      </div>

      <div className={recordFieldRowClass}>
        <span style={recordFieldLabel}>Start Date</span>
        <input
          type="date"
          value={hForm.start_date}
          onChange={(e) => updHousing((f) => ({...f, start_date: e.target.value}))}
          style={recordControl}
        />
      </div>

      <div className={recordFieldRowClass}>
        <span style={recordFieldLabel}>Status</span>
        <select
          value={hForm.status}
          onChange={(e) => updHousing((f) => ({...f, status: e.target.value}))}
          style={recordControl}
        >
          <option value="active">Active</option>
          <option value="retired">Retired</option>
        </select>
      </div>

      {hForm.status === 'retired' && (
        <div className={recordFieldRowClass}>
          <span style={recordFieldLabel}>Retired Date</span>
          <input
            type="date"
            value={hForm.retired_date}
            onChange={(e) => updHousing((f) => ({...f, retired_date: e.target.value}))}
            style={recordControl}
          />
        </div>
      )}
    </>
  );
  const notesEditor = (
    <>
      <div className={recordFieldRowClass}>
        <span style={recordFieldLabel}>Notes</span>
        <textarea
          value={hForm.notes}
          onChange={(e) => updHousing((f) => ({...f, notes: e.target.value}))}
          rows={3}
          style={recordTextarea}
        />
      </div>
    </>
  );
  const editorNotices = (
    <>
      {err && <div style={{color: 'var(--danger-ink)', fontSize: 12, fontWeight: 600, marginTop: 8}}>{err}</div>}
      <PigConflictNotice conflict={housingConflict} />
      {housingSaveState === 'failed' && (
        <InlineNotice notice={{kind: 'error', message: 'Changes have not been saved. Your draft is still here.'}} />
      )}
      {(housingSaveState === 'failed' || housingSaveState === 'invalid') && (
        <Button variant="secondary" onClick={() => void flushHousingAutosave()}>
          Retry Save
        </Button>
      )}
    </>
  );

  return (
    <RecordPageFrame Header={Header}>
      <RecordPageBody maxWidth={960} data-layer-housing-record-loaded="true">
        <RecordBackLink label={backLabel} onBack={() => navigate(backTarget)} />

        <RecordSequenceNav seq={recordSeq} currentId={housingId} onNavigate={navigateSeq} />
        <div style={{display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12}}>
          <RecordTitle margin={0}>
            {housingDot ? (
              <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}>
                <GroupDot color={housingDot} size={10} />
                {housing.housing_name}
              </span>
            ) : (
              housing.housing_name
            )}
          </RecordTitle>
          <Badge variant={isActive ? 'ok' : 'neutral'}>{housing.status}</Badge>
          {parentBatch && (
            <Button type="button" onClick={() => navigate('/layer/batches/' + parentBatch.id)}>
              Batch: {parentBatch.name}
            </Button>
          )}
          {housing.start_date && (
            <span style={{fontSize: 12, color: 'var(--ink-muted)'}}>from {fmt(housing.start_date)}</span>
          )}
          {housing.retired_date && (
            <span style={{fontSize: 12, color: 'var(--ink-faint)'}}>{'→ ' + fmt(housing.retired_date)}</span>
          )}
          {/* R25: the header's one action while a card is open. Done is the
              former modal's close: flush, and close only once it confirmed. */}
          {editingCard !== null && (
            <Button
              variant="primary"
              type="button"
              onClick={() => void closeForm()}
              disabled={housingActionBusy}
              data-layer-housing-done={housing.id}
              style={{marginLeft: 'auto'}}
            >
              Done
            </Button>
          )}
        </div>

        {notice && <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />}

        {/* R08: the record card is radius 18 on --shadow-card. */}
        <div
          style={{
            background: layerGroupTint(housingDot) ?? 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-card)',
            boxShadow: 'var(--shadow-card)',
            padding: '14px 18px',
            marginBottom: 12,
          }}
        >
          <div
            style={{display: 'flex', gap: 16, fontSize: 12, color: 'var(--ink)', flexWrap: 'wrap', marginBottom: 10}}
          >
            <span>
              Live hens:{' '}
              {liveHens == null ? (
                <strong style={{color: 'var(--ink)'}}>—</strong>
              ) : liveHensLow ? (
                <StatusText tone="danger">{liveHens}</StatusText>
              ) : (
                <strong style={{color: 'var(--text-primary)'}}>{liveHens}</strong>
              )}
            </span>
            {showAnchorCtx && (
              <span
                style={{color: 'var(--ink-muted)'}}
                title={
                  'Physical anchor ' +
                  proj.anchor +
                  (proj.anchorDate ? ' on ' + fmt(proj.anchorDate) : '') +
                  (proj.mortSince > 0 ? ', minus ' + proj.mortSince + ' mortalities since' : '')
                }
              >
                anchor <strong style={{color: 'var(--ink)'}}>{proj.anchor}</strong>
                {proj.mortSince > 0 ? ' (−' + proj.mortSince + ')' : ''}
                {proj.anchorDate ? ' · ' + fmt(proj.anchorDate) : ''}
              </span>
            )}
            <span>
              Capacity: <strong style={{color: 'var(--ink)'}}>{cap === 9999 ? 'Unlimited' : cap}</strong>
            </span>
            {util !== null && (
              <span>
                Utilization:{' '}
                <StatusText tone={util > 95 ? 'danger' : util > 80 ? 'warn' : 'ok'}>{util + '%'}</StatusText>
              </span>
            )}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))',
              gap: 8,
              marginBottom: 10,
            }}
          >
            {(
              [
                [
                  'Total Feed',
                  Number(hs.totalFeed) > 0 ? Math.round(Number(hs.totalFeed)).toLocaleString() + ' lbs' : '—',
                  'var(--text-primary)',
                ],
                [
                  'Starter',
                  Number(hs.starterFeed) > 0 ? Math.round(Number(hs.starterFeed)).toLocaleString() + ' lbs' : '—',
                  'var(--text-primary)',
                ],
                [
                  'Grower',
                  Number(hs.growerFeed) > 0 ? Math.round(Number(hs.growerFeed)).toLocaleString() + ' lbs' : '—',
                  'var(--text-primary)',
                ],
                [
                  'Layer',
                  Number(hs.layerFeed) > 0 ? Math.round(Number(hs.layerFeed)).toLocaleString() + ' lbs' : '—',
                  'var(--text-primary)',
                ],
                // Mortality reads as a loss signal above the threshold; otherwise black.
                [
                  'Mortality',
                  hs.totalMort || '0',
                  Number(hs.totalMort) > 5 ? 'var(--danger-ink)' : 'var(--text-primary)',
                ],
                ['Eggs', Number(hs.totalEggs) > 0 ? Number(hs.totalEggs).toLocaleString() : '—', 'var(--text-primary)'],
                // [label, value, colour]. Named as a tuple because the inferred
                // element type would be (string | number)[] — the Mortality tile's
                // value falls back to hs.totalMort, a number — and that would make
                // `color` string|number, which is not a CSS colour.
              ] as [string, string | number, string][]
            ).map(([label, val, color]) => (
              <div
                key={label}
                style={{
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  padding: '10px 12px',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    color: 'var(--ink-muted)',
                    marginBottom: 4,

                    letterSpacing: 0.5,
                  }}
                >
                  {label}
                </div>
                <div style={{fontSize: 15, fontWeight: 700, color}}>{val}</div>
              </div>
            ))}
          </div>

          {/* Retire is the housing's own action (R10 keeps it): same role and
              status gates, same confirmation. */}
          {canEdit && isActive && (
            <div style={{display: 'flex', gap: 8, marginTop: 12}}>
              <Button
                disabled={housingActionBusy}
                type="button"
                onClick={confirmRetire}
                data-layer-housing-retire={housing.id}
              >
                Retire
              </Button>
            </div>
          )}
        </div>

        {/* R25: details, read first; Edit flips this card to inputs in place. */}
        <div style={{...recordCardS, padding: '16px 20px', marginBottom: 12}} data-layer-housing-card="details">
          {cardHead('details', 'Housing Details')}
          {editingCard === 'details' ? (
            <fieldset disabled={housingActionBusy} style={editorFieldsetS} {...editorAttrs('details')}>
              {detailsEditor}
              {editorNotices}
            </fieldset>
          ) : (
            <dl className="poultry-fact-rows">
              <div>
                <dt>Current Count</dt>
                <dd>
                  {housing.current_count != null ? housing.current_count : '—'}
                  {housing.current_count_date ? ' · set ' + fmt(housing.current_count_date) : ''}
                </dd>
              </div>
              <div>
                <dt>Start Date</dt>
                <dd>{housing.start_date ? fmt(housing.start_date) : '—'}</dd>
              </div>
              {housing.status === 'retired' && (
                <div>
                  <dt>Retired Date</dt>
                  <dd>{housing.retired_date ? fmt(housing.retired_date) : '—'}</dd>
                </div>
              )}
            </dl>
          )}
        </div>

        {/* R25: notes, read first. An empty note is the muted dash (R14). */}
        <div style={{...recordCardS, padding: '16px 20px', marginBottom: 12}} data-layer-housing-card="notes">
          {cardHead('notes', 'Notes')}
          {editingCard === 'notes' ? (
            <fieldset disabled={housingActionBusy} style={editorFieldsetS} {...editorAttrs('notes')}>
              {notesEditor}
              {editorNotices}
            </fieldset>
          ) : (
            <div style={{fontSize: 13, color: housing.notes ? 'var(--ink)' : 'var(--label)', whiteSpace: 'pre-wrap'}}>
              {housing.notes || '—'}
            </div>
          )}
        </div>

        <RecordCollaborationSection
          sb={sb}
          authState={authState}
          entityType="layer.housing"
          entityId={housing.id}
          entityLabel={housing.housing_name as string}
        />
      </RecordPageBody>
    </RecordPageFrame>
  );
}
